import { MapMouseEvent } from 'maplibre-gl';
import { GDFPalletCode } from '../../gdf/type';
import { WeatherPickingInfoLayer } from '../../manager/types';
import { getPalette, Palette } from '../../palettes/palettes';
import products from '../../products/products';
import { WeatherSettingsChangeSource } from '../../settings/settings';
import { generateRampFromPalette } from '../../utils/ramp';
import { MessageType } from '../../worker/actor/actor-messages';
import { ImageLayer } from '../custom/image/image-layer';
import { WeatherLayerBase } from '../weather-layer-base';

const RADAR_PALETTES = [
    GDFPalletCode.nexrad16,
    GDFPalletCode.nexrad48,
    GDFPalletCode.nexrad64,
    GDFPalletCode.echotops,
    GDFPalletCode.precip
];

const SATELLITE_PALLETTES = [
    GDFPalletCode.satir_256,
    GDFPalletCode.satir_256t,
    GDFPalletCode.satir_256n,
    GDFPalletCode.satvis_256f,
    GDFPalletCode.satwv_256f,
    GDFPalletCode.satellite
];

const TURBULENCE_PALETTES = [GDFPalletCode.gtg, GDFPalletCode.turb_cat, GDFPalletCode.turb_edr];

export class RasterSingleCustomLayer extends WeatherLayerBase {
    private layer: ImageLayer | null = null;
    private response:
        | {
              data: Uint8Array;
              bounds: number[];
              width: number;
              height: number;
          }
        | null
        | undefined = null;

    private palette: Palette | null = null;

    protected onAdd(): void {
        this.layer = new ImageLayer(this.baseId);
        this.map.addLayer(this.layer, this.slotTopLayerId);
    }

    protected onRemove(): void {
        this.map.removeLayer(this.baseId);
    }

    protected onDisplayChanged(): void {
        this.map.setLayoutProperty(this.baseId, 'visibility', this.shouldDisplay() ? 'visible' : 'none');
    }

    protected async loadFrame(frameId: number) {
        this.response = await this.dispatcher.dispatch({
            type: MessageType.getRasterA,
            data: {
                id: this.productId,
                frame: frameId,
                workspaceId: this.workspaceId
            }
        });

        if (!this.response) {
            return;
        }

        this.layer?.updateBounds(this.response.bounds as [number, number, number, number]);
        this.layer?.updateImage(this.response.data, this.response.width, this.response.height, 'nearest');

        this.loadSettings();
    }

    protected async loadProduct(): Promise<void> {
        // All frames should have the same palette code so it should be safe to do [0]
        // TODO: Here we could preload all frames
        const code = this.info!.frames[0].header.productPallet;
        this.palette = getPalette(code, this.settings);
        if (!this.palette) {
            return;
        }
        const ramp = generateRampFromPalette(this.palette);
        this.layer?.updateRamp(ramp, this.settings.rasterInterpolation.paletteInterpolation);
    }

    protected onSettingsUpdated(changeSource: WeatherSettingsChangeSource): void {
        if (
            changeSource === WeatherSettingsChangeSource.radarSatellite ||
            changeSource === WeatherSettingsChangeSource.turbulenceTransparency ||
            changeSource === WeatherSettingsChangeSource.polygonTransparency
        ) {
            this.loadSettings();
        }
    }

    private loadSettings() {
        if (!this.layer || !this.palette) {
            return;
        }

        if (this.palette.id === GDFPalletCode.nexrad16 || this.palette.id === GDFPalletCode.nexrad64) {
            this.layer.smoothing = {
                enabled: this.settings.radar.radarSmoothing,
                linear: true,
                groupSize: 255
            };
        } else if (this.palette.id === GDFPalletCode.nexrad48) {
            this.layer.smoothing = {
                enabled: this.settings.radar.radarSmoothing,
                linear: false,
                groupSize: 16
            };
        }

        if (
            this.palette.id === GDFPalletCode.nexrad16 ||
            this.palette.id === GDFPalletCode.nexrad48 ||
            this.palette.id === GDFPalletCode.nexrad64
        ) {
            this.layer.minLevel = Math.max(0, this.settings.radar.nexradMinLevel - 1) / 255;
        } else if (this.palette.id === GDFPalletCode.echotops) {
            this.layer.minLevel = Math.max(0, this.settings.radar.nexradEchoTopsMinLevel - 1) / 255;
        } else if (this.palette.id === GDFPalletCode.precip) {
            this.layer.minLevel = Math.max(0, this.settings.radar.nexradRainfallMinLevel - 1) / 255;
        }

        // Satellite
        else if (this.palette.id === GDFPalletCode.satir_256) {
            this.layer.minLevel = this.settings.radar.satelliteMinLevel / 127;
        } else if (this.palette.id === GDFPalletCode.satir_256t) {
            this.layer.minLevel = (-this.settings.radar.satelliteMinTemp + 30) / 120;
        } else if (this.palette.id === GDFPalletCode.satir_256n) {
            this.layer.minLevel = this.settings.radar.satelliteMinLevel / 55;
        } else if (this.palette.id === GDFPalletCode.satvis_256f) {
            this.layer.minLevel = this.settings.radar.satelliteMinLevel / 127;
        } else if (this.palette.id === GDFPalletCode.satwv_256f) {
            this.layer.minLevel = this.settings.radar.satelliteMinLevel / 127;
        } else if (this.palette.id === GDFPalletCode.satellite) {
            const min = this.settings.radar.satelliteMinLevel;
            this.layer.minLevel = Math.max(0, min - 1) / 255;
        }

        const isRadar = RADAR_PALETTES.includes(this.palette.id);
        const isSatellite = SATELLITE_PALLETTES.includes(this.palette.id);
        const isTurb = TURBULENCE_PALETTES.includes(this.palette.id);

        if (isRadar) {
            this.layer.opacity = this.settings.radar.enableRadarTransparency ? this.settings.radar.radarTransparency / 100 : 1.0;
        } else if (isSatellite) {
            this.layer.opacity = this.settings.radar.enableSatelliteTransparency ? this.settings.radar.satelliteTransparency / 100 : 1.0;
        } else if (isTurb) {
            this.layer.opacity =
                this.settings.misc.enablePolygonTransparency && this.settings.turbulenceIcing.turbulenceApplyTransparency
                    ? this.settings.misc.polygonTransparency / 100
                    : 1.0;
        }
    }

    async pickObjects(point: MapMouseEvent): Promise<WeatherPickingInfoLayer | null> {
        // TODO: check image bounds first

        if (!this.response || !this.palette) {
            return null;
        }

        const lngLat = point.lngLat;
        const lngWidth = this.response.bounds[2] - this.response.bounds[0];
        const latHeight = this.response.bounds[3] - this.response.bounds[1];

        const colPerDeg = this.response.width / lngWidth;
        const rowPerDeg = this.response.height / latHeight;
        const x = Math.round((lngLat.lng - this.response.bounds[0]) * colPerDeg);
        const y = Math.round((lngLat.lat - this.response.bounds[1]) * rowPerDeg);

        if (x < 0 || x > this.response.width || y < 0 || y > this.response.height) {
            return null;
        }

        const pixel = this.response.data[(this.response.height - y) * this.response.width + x];

        if (pixel / 255 <= this.layer!.minLevel) {
            return null;
        }

        return {
            product: products[this.productId],
            palette: this.palette.id,
            objects: [
                {
                    type: 'raster',
                    value: pixel,
                    palette: this.palette
                }
            ]
        };
    }
}
